<template>
  <div class="my-subscriptions-page">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-package-variant-closed</v-icon>
                My Subscriptions
                <span class="page-inline-subtitle">Manage your AI agents subscriptions and billing</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  color="success" 
                  @click="openCustomSubscriptionDialog"
                  class="modern-btn mr-2"
                  elevation="2"
                >
                  <v-icon left>mdi-plus-circle</v-icon>
                  Custom Subscription
                </v-btn>
                <v-btn 
                  color="primary" 
                  to="/available-subscriptions"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-store</v-icon>
                  Browse Plans
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Subscriptions -->
      <v-row v-if="subscriptions.length > 0">
        <v-col
          v-for="subscription in subscriptions"
          :key="subscription.id"
          cols="12"
          md="6"
          lg="4"
        >
          <v-card class="subscription-card" elevation="8">
            <div class="subscription-header" :class="getStatusClass(subscription.status)">
              <div class="plan-badge" v-if="subscription.is_popular">
                <v-icon small>mdi-star</v-icon>
                Popular
              </div>
              <div class="plan-name">{{ subscription.name }}</div>
              <div class="plan-status">
                <v-chip 
                  :color="getStatusColor(subscription.status)" 
                  dark 
                  small
                >
                  {{ subscription.status }}
                </v-chip>
              </div>
            </div>

            <v-card-text class="subscription-content pa-4">
              <!-- Subscription Details -->
              <div class="subscription-details mb-4">
                <div class="detail-item">
                  <span class="detail-label">Billing Period:</span>
                  <span class="detail-value">{{ subscription.billing_period || 'Monthly' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Next Billing:</span>
                  <span class="detail-value">{{ formatDate(subscription.next_billing_date) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Started:</span>
                  <span class="detail-value">{{ formatDate(subscription.start_date) }}</span>
                </div>
              </div>

              <!-- AI Agents List -->
              <div class="agents-section mb-4">
                <h4 class="section-title">
                  <v-icon small class="mr-2">mdi-robot</v-icon>
                  Included Agents ({{ subscription.agents?.length || 0 }})
                </h4>
                <div class="agents-list">
                  <div
                    v-for="agent in subscription.agents"
                    :key="agent.id"
                    class="agent-item"
                  >
                    <v-avatar size="24" class="mr-2">
                      <v-img
                        :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                        :alt="agent.name"
                      />
                    </v-avatar>
                    <span class="agent-name">{{ agent.name }}</span>
                    <v-spacer></v-spacer>
                    <v-chip color="success" outlined x-small>
                      <v-icon left x-small>mdi-check</v-icon>
                      Active
                    </v-chip>
                  </div>
                </div>
              </div>

              <!-- Pricing Info -->
              <div class="pricing-section">
                <div class="pricing-row">
                  <span>Subscription Price:</span>
                  <span class="price-value">${{ subscription.price || 0 }}/{{ subscription.billing_period?.toLowerCase() || 'month' }}</span>
                </div>
                <div class="pricing-row" v-if="subscription.discount_amount">
                  <span>Discount Applied:</span>
                  <span class="discount-value">-${{ subscription.discount_amount }}</span>
                </div>
                <div class="pricing-row total">
                  <span>Total:</span>
                  <span class="total-value">${{ calculateTotal(subscription) }}/{{ subscription.billing_period?.toLowerCase() || 'month' }}</span>
                </div>
              </div>
            </v-card-text>

            <v-card-actions class="subscription-actions pa-4">
              <div class="action-buttons">
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="viewDetails(subscription)"
                >
                  <v-icon left small>mdi-eye</v-icon>
                  View Details
                </v-btn>
                <v-btn
                  small
                  color="error"
                  outlined
                  v-if="subscription.status === 'active'"
                  @click="cancelSubscription(subscription)"
                  :loading="cancellingId === subscription.id"
                >
                  <v-icon left small>mdi-cancel</v-icon>
                  Cancel
                </v-btn>
              </div>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <v-icon size="64" color="#64748b">mdi-package-variant-closed-remove</v-icon>
        <h2>No Active Subscriptions</h2>
        <p>You don't have any active subscriptions yet. Browse our available plans to get started with AI agents.</p>
        <v-btn color="primary" to="/available-subscriptions" class="mt-4 modern-btn">
          <v-icon left>mdi-store</v-icon>
          Browse Available Plans
        </v-btn>
      </div>

      <!-- Usage Statistics -->
      <v-row v-if="subscriptions.length > 0" class="mt-6">
        <v-col cols="12">
          <v-card class="usage-stats-card" elevation="6">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-chart-line</v-icon>
                Usage Statistics
              </h2>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-item">
                    <div class="stat-icon">
                      <v-icon size="32" color="#667eea">mdi-robot</v-icon>
                    </div>
                    <div class="stat-content">
                      <div class="stat-value">{{ totalAgents }}</div>
                      <div class="stat-label">Total AI Agents</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-item">
                    <div class="stat-icon">
                      <v-icon size="32" color="#10b981">mdi-calendar-check</v-icon>
                    </div>
                    <div class="stat-content">
                      <div class="stat-value">{{ activeDays }}</div>
                      <div class="stat-label">Days Active</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-item">
                    <div class="stat-icon">
                      <v-icon size="32" color="#f59e0b">mdi-currency-usd</v-icon>
                    </div>
                    <div class="stat-content">
                      <div class="stat-value">${{ monthlySpend }}</div>
                      <div class="stat-label">Monthly Spend</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-item">
                    <div class="stat-icon">
                      <v-icon size="32" color="#8b5cf6">mdi-trending-up</v-icon>
                    </div>
                    <div class="stat-content">
                      <div class="stat-value">{{ savingsPercent }}%</div>
                      <div class="stat-label">Savings vs Individual</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Past Subscriptions Table -->
      <v-row v-if="pastSubscriptions.length > 0" class="mt-6">
        <v-col cols="12">
          <v-card class="past-subscriptions-card" elevation="6">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-history</v-icon>
                Past Subscriptions
              </h2>
              <v-spacer></v-spacer>
              <v-chip color="grey" outlined small>
                {{ pastSubscriptions.length }} {{ pastSubscriptions.length === 1 ? 'subscription' : 'subscriptions' }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="pastSubscriptionsHeaders"
                :items="pastSubscriptions"
                class="past-subscriptions-table"
                :items-per-page="10"
                :footer-props="{
                  'items-per-page-options': [5, 10, 25, 50]
                }"
              >
                <!-- Plan Name -->
                <template v-slot:[`item.name`]="{ item }">
                  <div class="plan-info">
                    <div class="plan-name-cell">{{ item.name }}</div>
                    <v-chip v-if="item.is_custom" color="purple" outlined x-small class="mt-1">
                      Custom
                    </v-chip>
                  </div>
                </template>

                <!-- Status -->
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip 
                    :color="getStatusColor(item.status)" 
                    dark 
                    small
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <!-- Agents -->
                <template v-slot:[`item.agents`]="{ item }">
                  <div class="agents-cell">
                    <v-chip
                      v-for="agent in item.agents.slice(0, 2)"
                      :key="agent.id"
                      color="primary"
                      outlined
                      x-small
                      class="ma-1"
                    >
                      {{ agent.name }}
                    </v-chip>
                    <v-chip
                      v-if="item.agents.length > 2"
                      color="grey"
                      outlined
                      x-small
                      class="ma-1"
                    >
                      +{{ item.agents.length - 2 }} more
                    </v-chip>
                  </div>
                </template>

                <!-- Duration -->
                <template v-slot:[`item.duration`]="{ item }">
                  <div class="duration-cell">
                    <div class="duration-text">{{ calculateDuration(item) }}</div>
                    <div class="date-range">
                      {{ formatDate(item.start_date) }} - {{ formatDate(item.end_date) }}
                    </div>
                  </div>
                </template>

                <!-- Price -->
                <template v-slot:[`item.price`]="{ item }">
                  <div class="price-cell">
                    <span class="price-value">${{ item.price }}</span>
                    <span class="price-period">/month</span>
                  </div>
                </template>

                <!-- Actions -->
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="viewDetails(item)"
                  >
                    <v-icon left small>mdi-eye</v-icon>
                    View
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Subscription Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="800px">
      <v-card class="details-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">Subscription Details</h2>
              <p class="dialog-subtitle">{{ selectedSubscription?.name }}</p>
            </div>
            <v-btn 
              icon 
              @click="showDetailsDialog = false"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <div v-if="selectedSubscription">
            <!-- Subscription Information -->
            <div class="detail-section mb-6">
              <h3 class="section-title">Subscription Information</h3>
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Plan Name</div>
                    <div class="info-value">{{ selectedSubscription.name }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Status</div>
                    <v-chip 
                      :color="getStatusColor(selectedSubscription.status)" 
                      dark 
                      small
                    >
                      {{ selectedSubscription.status }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Start Date</div>
                    <div class="info-value">{{ formatDate(selectedSubscription.start_date) }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Next Billing</div>
                    <div class="info-value">{{ formatDate(selectedSubscription.next_billing_date) }}</div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Billing Information -->
            <div class="detail-section">
              <h3 class="section-title">Billing Information</h3>
              <div class="billing-summary">
                <div class="billing-item">
                  <span>Base Price:</span>
                  <span>${{ selectedSubscription.price || 0 }}</span>
                </div>
                <div class="billing-item" v-if="selectedSubscription.discount_amount">
                  <span>Discount:</span>
                  <span class="discount">-${{ selectedSubscription.discount_amount }}</span>
                </div>
                <div class="billing-item total">
                  <span>Total:</span>
                  <span>${{ calculateTotal(selectedSubscription) }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showDetailsDialog = false"
            class="cancel-btn"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Custom Subscription Dialog -->
    <v-dialog v-model="showCustomDialog" max-width="1200px" persistent>
      <v-card class="custom-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">
                <v-icon large class="mr-3">mdi-package-variant-plus</v-icon>
                Create Custom Subscription
              </h2>
              <p class="dialog-subtitle">Build a personalized subscription plan by selecting specific AI agents</p>
            </div>
            <v-btn 
              icon 
              @click="closeCustomDialog"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <div class="dialog-content">
          <!-- Progress Stepper -->
          <div class="progress-stepper">
            <div class="stepper-header">
              <div class="step" :class="{ 'active': currentStep >= 1, 'completed': currentStep > 1 }">
                <div class="step-number">1</div>
                <div class="step-label">Select Agents</div>
              </div>
              <div class="step-divider"></div>
              <div class="step" :class="{ 'active': currentStep >= 2, 'completed': currentStep > 2 }">
                <div class="step-number">2</div>
                <div class="step-label">Configure</div>
              </div>
              <div class="step-divider"></div>
              <div class="step" :class="{ 'active': currentStep >= 3 }">
                <div class="step-number">3</div>
                <div class="step-label">Review</div>
              </div>
            </div>
          </div>

          <!-- Step 1: Agent Selection -->
          <div v-show="currentStep === 1" class="step-content">
            <div class="step-header">
              <h3 class="step-title">
                <v-icon large class="mr-2">mdi-robot</v-icon>
                Select AI Agents
              </h3>
              <p class="step-description">Choose the AI agents you want to include in your custom subscription</p>
            </div>

            <div class="agents-section">
              <!-- Search and Filters -->
              <div class="agents-toolbar">
                <div class="search-container">
                  <v-text-field
                    v-model="agentSearch"
                    outlined
                    placeholder="Search AI agents by name or industry..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    dense
                    class="search-input"
                    hide-details
                  ></v-text-field>
                </div>
                <div class="selection-info">
                  <v-chip 
                    v-if="customForm.selected_agents.length > 0" 
                    color="success" 
                    outlined
                    class="selection-chip"
                  >
                    <v-icon left small>mdi-check-circle</v-icon>
                    {{ customForm.selected_agents.length }} agent{{ customForm.selected_agents.length !== 1 ? 's' : '' }} selected
                  </v-chip>
                  <v-chip v-else color="grey" outlined class="selection-chip">
                    <v-icon left small>mdi-information</v-icon>
                    No agents selected
                  </v-chip>
                </div>
              </div>

              <!-- Agents Grid -->
              <div class="agents-container">
                <div v-if="filteredAgents.length > 0" class="agents-grid">
                  <div
                    v-for="agent in filteredAgents"
                    :key="agent.id"
                    class="agent-card"
                    :class="{ 'selected': customForm.selected_agents.includes(agent.id) }"
                    @click="toggleAgent(agent.id)"
                  >
                    <div class="agent-card-header">
                      <v-checkbox
                        :value="customForm.selected_agents.includes(agent.id)"
                        @click.stop="toggleAgent(agent.id)"
                        color="success"
                        hide-details
                        class="agent-checkbox"
                      ></v-checkbox>
                      <v-chip 
                        v-if="customForm.selected_agents.includes(agent.id)"
                        color="success"
                        x-small
                        class="selected-badge"
                      >
                        Selected
                      </v-chip>
                    </div>
                    
                    <div class="agent-card-body">
                      <v-avatar size="50" class="mb-3">
                        <v-img
                          :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                          :alt="agent.name"
                        />
                      </v-avatar>
                      
                      <div class="agent-info">
                        <h4 class="agent-name">{{ agent.name }}</h4>
                        <p class="agent-industry" v-if="agent.industry">
                          {{ agent.industry.name }}
                        </p>
                        <div class="agent-pricing">
                          <span class="price">${{ agent.monthly_price || 0 }}</span>
                          <span class="period">/month</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="isLoadingAgents" class="loading-state">
                  <v-progress-circular indeterminate color="primary" size="60"></v-progress-circular>
                  <h3 class="mt-4">Loading AI Agents...</h3>
                  <p>Please wait while we fetch available agents</p>
                </div>

                <div v-else class="empty-state">
                  <v-icon size="80" color="#94a3b8">mdi-robot-off</v-icon>
                  <h3 class="mt-4">No Agents Found</h3>
                  <p>No AI agents match your search criteria</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Configuration -->
          <div v-show="currentStep === 2" class="step-content">
            <div class="step-header">
              <h3 class="step-title">
                <v-icon large class="mr-2">mdi-cog</v-icon>
                Configure Subscription
              </h3>
              <p class="step-description">Set up subscription details and preferences</p>
            </div>

            <v-row class="config-section">
              <v-col cols="12" md="6">
                <div class="config-card">
                  <h4 class="config-title">Subscription Timeline</h4>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="customForm.start_date"
                        outlined
                        label="Start Date *"
                        prepend-inner-icon="mdi-calendar-start"
                        :error-messages="customErrors.start_date"
                        class="config-input"
                        type="date"
                        hide-details="auto"
                        :min="today"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="customForm.end_date"
                        outlined
                        label="End Date (Optional)"
                        prepend-inner-icon="mdi-calendar-end"
                        :error-messages="customErrors.end_date"
                        class="config-input"
                        type="date"
                        hide-details="auto"
                        :min="customForm.start_date"
                        hint="Leave empty for ongoing subscription"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="config-card">
                  <h4 class="config-title">Additional Options</h4>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="customForm.coupon_code"
                        :items="coupons"
                        outlined
                        label="Apply Coupon (Optional)"
                        prepend-inner-icon="mdi-tag-outline"
                        class="config-input"
                        clearable
                        hide-details="auto"
                        hint="Select a coupon to apply discount"
                        persistent-hint
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="customForm.notes"
                        outlined
                        label="Notes"
                        prepend-inner-icon="mdi-note-text-outline"
                        :error-messages="customErrors.notes"
                        class="config-input"
                        rows="3"
                        hide-details="auto"
                        placeholder="Add any special notes or requirements..."
                        hint="Internal notes for this subscription"
                        persistent-hint
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Step 3: Review -->
          <div v-show="currentStep === 3" class="step-content">
            <div class="step-header">
              <h3 class="step-title">
                <v-icon large class="mr-2">mdi-clipboard-check</v-icon>
                Review & Confirm
              </h3>
              <p class="step-description">Review your custom subscription before creating it</p>
            </div>

            <div class="review-section">
              <v-row>
                <v-col cols="12" md="8">
                  <!-- Selected Agents Review -->
                  <div class="review-card">
                    <h4 class="review-title">
                      <v-icon class="mr-2">mdi-robot</v-icon>
                      Selected AI Agents ({{ customForm.selected_agents.length }})
                    </h4>
                    <div class="selected-agents-list">
                      <div 
                        v-for="agent in getSelectedAgents()" 
                        :key="agent.id"
                        class="selected-agent-item"
                      >
                        <v-avatar size="40" class="mr-3">
                          <v-img
                            :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                            :alt="agent.name"
                          />
                        </v-avatar>
                        <div class="agent-details">
                          <h5 class="agent-name">{{ agent.name }}</h5>
                          <p class="agent-industry" v-if="agent.industry">{{ agent.industry.name }}</p>
                        </div>
                        <div class="agent-price">
                          <span class="price">${{ agent.monthly_price || 0 }}</span>
                          <span class="period">/mo</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Subscription Details Review -->
                  <div class="review-card mt-4">
                    <h4 class="review-title">
                      <v-icon class="mr-2">mdi-information</v-icon>
                      Subscription Details
                    </h4>
                    <div class="details-grid">
                      <div class="detail-item">
                        <span class="detail-label">Start Date:</span>
                        <span class="detail-value">{{ formatDateForDisplay(customForm.start_date) }}</span>
                      </div>
                      <div class="detail-item" v-if="customForm.end_date">
                        <span class="detail-label">End Date:</span>
                        <span class="detail-value">{{ formatDateForDisplay(customForm.end_date) }}</span>
                      </div>
                      <div class="detail-item" v-else>
                        <span class="detail-label">Duration:</span>
                        <span class="detail-value">Ongoing subscription</span>
                      </div>
                      <div class="detail-item" v-if="customForm.coupon_code">
                        <span class="detail-label">Coupon Applied:</span>
                        <span class="detail-value">{{ customForm.coupon_code }}</span>
                      </div>
                      <div class="detail-item" v-if="customForm.notes">
                        <span class="detail-label">Notes:</span>
                        <span class="detail-value">{{ customForm.notes }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <!-- Pricing Summary -->
                  <div class="pricing-card">
                    <h4 class="pricing-title">
                      <v-icon class="mr-2">mdi-calculator</v-icon>
                      Pricing Summary
                    </h4>
                    <div class="pricing-breakdown">
                      <div class="pricing-row">
                        <span>Base Price ({{ customForm.selected_agents.length }} agents):</span>
                        <span class="amount">${{ calculateCustomTotal() }}</span>
                      </div>
                      <div v-if="appliedDiscount > 0" class="pricing-row discount">
                        <span>Discount Applied:</span>
                        <span class="amount discount-amount">-${{ appliedDiscount }}</span>
                      </div>
                      <div class="pricing-row total">
                        <span>Total Monthly Cost:</span>
                        <span class="amount total-amount">${{ calculateFinalTotal() }}</span>
                      </div>
                      <div class="pricing-period">
                        <v-icon small class="mr-1">mdi-information</v-icon>
                        Billed monthly
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>

          <!-- Navigation -->
          <div class="dialog-navigation">
            <div class="nav-buttons">
              <v-btn
                v-if="currentStep > 1"
                outlined
                color="primary"
                @click="previousStep"
                class="nav-btn"
              >
                <v-icon left>mdi-chevron-left</v-icon>
                Previous
              </v-btn>
              
              <v-spacer></v-spacer>
              
              <v-btn
                text
                color="grey"
                @click="closeCustomDialog"
                class="nav-btn"
              >
                Cancel
              </v-btn>
              
              <v-btn
                v-if="currentStep < 3"
                color="primary"
                @click="nextStep"
                :disabled="!canProceed"
                class="nav-btn ml-2"
              >
                Next
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
              
              <v-btn
                v-else
                color="success"
                @click="createCustomSubscription"
                :loading="isCreating"
                :disabled="customForm.selected_agents.length === 0"
                class="nav-btn ml-2"
              >
                <v-icon left>mdi-check</v-icon>
                Create Subscription
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
      class="notification-snackbar"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "MySubscriptionsPage",
  data() {
    return {
      subscriptions: [],
      pastSubscriptions: [],
      isLoading: false,
      showDetailsDialog: false,
      selectedSubscription: null,
      cancellingId: null,
      showCustomDialog: false,
      isCreating: false,
      isLoadingAgents: false,
      availableAgents: [],
      coupons: [],
      agentSearch: '',
      currentStep: 1,
      customForm: {
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        coupon_code: '',
        notes: '',
        selected_agents: []
      },
      customErrors: {},
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000
      },
      pastSubscriptionsHeaders: [
        { text: 'Plan', value: 'name', sortable: true, width: '200px' },
        { text: 'Status', value: 'status', sortable: true, width: '120px' },
        { text: 'AI Agents', value: 'agents', sortable: false, width: '250px' },
        { text: 'Duration', value: 'duration', sortable: false, width: '200px' },
        { text: 'Price', value: 'price', sortable: true, width: '120px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '100px', align: 'center' },
      ],
    };
  },
  computed: {
    totalAgents() {
      return this.subscriptions.reduce((total, sub) => {
        return total + (sub.agents?.length || 0);
      }, 0);
    },
    activeDays() {
      if (this.subscriptions.length === 0) return 0;
      const oldestSubscription = this.subscriptions.reduce((oldest, sub) => {
        const subDate = new Date(sub.start_date);
        const oldestDate = new Date(oldest.start_date);
        return subDate < oldestDate ? sub : oldest;
      });
      const daysDiff = Math.floor((new Date() - new Date(oldestSubscription.start_date)) / (1000 * 60 * 60 * 24));
      return Math.max(0, daysDiff);
    },
    monthlySpend() {
      return this.subscriptions.reduce((total, sub) => {
        return total + this.calculateTotal(sub);
      }, 0);
    },
    savingsPercent() {
      // Calculate savings compared to individual agent pricing
      const individualTotal = this.subscriptions.reduce((total, sub) => {
        return total + (sub.original_price || sub.price || 0);
      }, 0);
      const currentTotal = this.monthlySpend;
      if (individualTotal === 0) return 0;
      return Math.round(((individualTotal - currentTotal) / individualTotal) * 100);
    },
    today() {
      return new Date().toISOString().split('T')[0];
    },
    filteredAgents() {
      if (!this.agentSearch) return this.availableAgents;
      const search = this.agentSearch.toLowerCase();
      return this.availableAgents.filter(agent => 
        agent.name.toLowerCase().includes(search) ||
        (agent.industry && agent.industry.name.toLowerCase().includes(search))
      );
    },
    appliedDiscount() {
      if (!this.customForm.coupon_code) return 0;
      // Calculate discount based on coupon
      const selectedTotal = this.calculateCustomTotal();
      // For now, apply a fixed 10% discount if coupon is applied
      return Math.round(selectedTotal * 0.1);
    },
    canProceed() {
      if (this.currentStep === 1) {
        return this.customForm.selected_agents.length > 0;
      }
      if (this.currentStep === 2) {
        return this.customForm.start_date;
      }
      return true;
    },
  },
  mounted() {
    this.fetchSubscriptions();
  },
  methods: {
    async fetchSubscriptions() {
      try {
        this.isLoading = true;
        const response = await axios.get('/my-subscriptions');
        this.subscriptions = response.data.active || [];
        this.pastSubscriptions = response.data.past || [];
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        this.subscriptions = [];
        this.pastSubscriptions = [];
        // Show error message to user
        this.showErrorMessage('Failed to load subscriptions. Please try again later.');
      } finally {
        this.isLoading = false;
      }
    },
    viewDetails(subscription) {
      this.selectedSubscription = subscription;
      this.showDetailsDialog = true;
    },
    async cancelSubscription(subscription) {
      if (confirm(`Are you sure you want to cancel the "${subscription.name}" subscription? This action cannot be undone.`)) {
        this.cancellingId = subscription.id;
        try {
          await axios.delete(`/company-subscriptions/${subscription.id}`);
          
          // Update the subscription status to cancelled
          subscription.status = 'cancelled';
          
          // Show success message
          this.showSuccessMessage('Subscription cancelled successfully');
          
          // Refresh the subscriptions list
          await this.fetchSubscriptions();
        } catch (error) {
          console.error('Error canceling subscription:', error);
          this.showErrorMessage('Failed to cancel subscription. Please try again.');
        } finally {
          this.cancellingId = null;
        }
      }
    },
    calculateTotal(subscription) {
      const price = subscription.price || 0;
      const discount = subscription.discount_amount || 0;
      return Math.max(0, price - discount);
    },
    calculateDuration(subscription) {
      if (!subscription.start_date || !subscription.end_date) {
        return 'N/A';
      }
      
      const startDate = new Date(subscription.start_date);
      const endDate = new Date(subscription.end_date);
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 30) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        const remainingDays = diffDays % 30;
        if (remainingDays === 0) {
          return `${months} month${months !== 1 ? 's' : ''}`;
        }
        return `${months}m ${remainingDays}d`;
      } else {
        const years = Math.floor(diffDays / 365);
        const remainingDays = diffDays % 365;
        const months = Math.floor(remainingDays / 30);
        return `${years}y ${months}m`;
      }
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString();
    },
    getStatusColor(status) {
      const colors = {
        active: 'success',
        paused: 'warning',
        cancelled: 'error',
        canceled: 'error',
        expired: 'error'
      };
      return colors[status] || 'grey';
    },
    getStatusClass(status) {
      return `status-${status}`;
    },
    openCustomSubscriptionDialog() {
      this.showCustomDialog = true;
      this.loadAgents();
      this.loadCoupons();
    },
    closeCustomDialog() {
      this.showCustomDialog = false;
      this.currentStep = 1;
      this.customForm = {
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        coupon_code: '',
        notes: '',
        selected_agents: []
      };
      this.customErrors = {};
      this.agentSearch = '';
    },
    nextStep() {
      if (this.canProceed && this.currentStep < 3) {
        this.currentStep++;
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    getSelectedAgents() {
      return this.availableAgents.filter(agent => 
        this.customForm.selected_agents.includes(agent.id)
      );
    },
    async loadAgents() {
      try {
        this.isLoadingAgents = true;
        const response = await axios.get('/ai-agents');
        this.availableAgents = response.data.data || response.data || [];
      } catch (error) {
        console.error('Error loading agents:', error);
        this.availableAgents = [];
        this.showErrorMessage('Failed to load AI agents. Please try again.');
      } finally {
        this.isLoadingAgents = false;
      }
    },
    async loadCoupons() {
      try {
        const response = await axios.get('/coupons/active');
        this.coupons = (response.data.data || response.data || []).map(coupon => ({
          text: `${coupon.code} (${coupon.discount_percentage}% off)`,
          value: coupon.code
        }));
      } catch (error) {
        console.error('Error loading coupons:', error);
        this.coupons = [];
      }
    },
    toggleAgent(agentId) {
      const index = this.customForm.selected_agents.indexOf(agentId);
      if (index === -1) {
        this.customForm.selected_agents.push(agentId);
      } else {
        this.customForm.selected_agents.splice(index, 1);
      }
    },
    calculateCustomTotal() {
      const selectedAgents = this.availableAgents.filter(agent => 
        this.customForm.selected_agents.includes(agent.id)
      );
      return selectedAgents.reduce((total, agent) => {
        return total + (agent.monthly_price || 0);
      }, 0);
    },
    calculateFinalTotal() {
      const baseTotal = this.calculateCustomTotal();
      const discount = this.appliedDiscount;
      return Math.max(0, baseTotal - discount);
    },
    async createCustomSubscription() {
      try {
        this.isCreating = true;
        this.customErrors = {};

        // Validate required fields
        if (!this.customForm.start_date) {
          this.customErrors.start_date = ['Start date is required'];
          this.showErrorMessage('Start date is required');
          return;
        }

        if (this.customForm.selected_agents.length === 0) {
          this.showErrorMessage('Please select at least one AI agent');
          return;
        }

        const payload = {
          subscription_id: null, // Custom subscription doesn't need a plan
          start_date: this.customForm.start_date,
          end_date: this.customForm.end_date || null,
          coupon_code: this.customForm.coupon_code || null,
          is_custom: true,
          selected_agents: this.customForm.selected_agents,
          notes: this.customForm.notes || null
        };

        await axios.post('/company-subscriptions', payload);
        
        this.showSuccessMessage('Custom subscription created successfully!');
        this.closeCustomDialog();
        await this.fetchSubscriptions();
      } catch (error) {
        console.error('Error creating custom subscription:', error);
        if (error.response && error.response.data && error.response.data.errors) {
          this.customErrors = error.response.data.errors;
          const validationErrors = Object.values(error.response.data.errors).flat();
          this.showErrorMessage(`Validation Error: ${validationErrors.join(', ')}`);
        } else if (error.response && error.response.data && error.response.data.message) {
          this.showErrorMessage(error.response.data.message);
        } else {
          this.showErrorMessage('Failed to create custom subscription. Please try again.');
        }
      } finally {
        this.isCreating = false;
      }
    },
    formatDateForDisplay(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'success',
        timeout: 5000
      };
    },
    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'error',
        timeout: 7000
      };
    },
  },
};
</script>

<style scoped>
.my-subscriptions-page {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

/* Subscription Cards */
.subscription-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.subscription-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2) !important;
}

.subscription-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  position: relative;
}

.subscription-header.status-active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.subscription-header.status-paused {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.subscription-header.status-canceled {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.plan-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.plan-name {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.plan-status {
  display: flex;
  align-items: center;
}

.subscription-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.subscription-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e2e8f0;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 14px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #64748b;
}

.detail-value {
  font-weight: 600;
  color: #2c3e50;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.agents-list {
  max-height: 120px;
  overflow-y: auto;
}

.agent-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.agent-item:last-child {
  border-bottom: none;
}

.agent-name {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.pricing-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  margin-top: auto;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 14px;
}

.pricing-row:last-child {
  margin-bottom: 0;
}

.pricing-row.total {
  border-top: 1px solid #e2e8f0;
  padding-top: 8px;
  margin-top: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.price-value {
  font-weight: 600;
  color: #10b981;
}

.discount-value {
  font-weight: 600;
  color: #f59e0b;
}

.total-value {
  font-weight: 700;
  color: #2c3e50;
  font-size: 16px;
}

.subscription-actions {
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 8px;
  width: 100%;
  flex-wrap: wrap;
}

.action-buttons .v-btn {
  flex: 1;
  min-width: 0;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.empty-state h2 {
  margin: 16px 0 8px 0;
  color: #2c3e50;
}

.empty-state p {
  max-width: 400px;
  margin: 0 auto 16px;
}

/* Usage Stats */
.usage-stats-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.stat-item:hover {
  background: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.stat-icon {
  background: rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Modern Button */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

/* Dialog Styles */
.details-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  background: #f8f9fa;
}

.detail-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.info-item {
  margin-bottom: 12px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

.billing-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.billing-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.billing-item:last-child {
  margin-bottom: 0;
}

.billing-item.total {
  border-top: 1px solid #e2e8f0;
  padding-top: 8px;
  margin-top: 8px;
  font-weight: 600;
  font-size: 16px;
  color: #2c3e50;
}

.billing-item .discount {
  color: #f59e0b;
  font-weight: 600;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

/* Past Subscriptions Table */
.past-subscriptions-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.past-subscriptions-table {
  background: transparent !important;
}

.past-subscriptions-table >>> .v-data-table__wrapper {
  background: white;
}

.past-subscriptions-table >>> thead tr th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  color: #2c3e50 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1) !important;
  padding: 16px 12px !important;
}

.past-subscriptions-table >>> tbody tr {
  transition: all 0.3s ease;
}

.past-subscriptions-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.past-subscriptions-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.plan-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.plan-name-cell {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.agents-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 250px;
}

.duration-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.duration-text {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.date-range {
  font-size: 12px;
  color: #64748b;
}

.price-cell {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.price-value {
  font-weight: 600;
  color: #10b981;
  font-size: 16px;
}

.price-period {
  font-size: 12px;
  color: #64748b;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
  
  .stat-item {
    padding: 12px;
  }
  
  .stat-value {
    font-size: 20px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .subscription-header {
    padding: 20px;
  }
  
  .plan-name {
    font-size: 20px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }
  
  .action-buttons .v-btn {
    width: 100%;
  }
  
  .stat-item {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
}

/* Custom Subscription Dialog */
.custom-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  max-height: 90vh;
}

.dialog-content {
  max-height: calc(90vh - 200px);
  overflow-y: auto;
}

/* Progress Stepper */
.progress-stepper {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.stepper-header {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #667eea;
  color: white;
}

.step.completed .step-number {
  background: #10b981;
  color: white;
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  transition: all 0.3s ease;
}

.step.active .step-label,
.step.completed .step-label {
  color: #2c3e50;
  font-weight: 600;
}

.step-divider {
  width: 100px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 20px;
  margin-top: -20px;
  transition: all 0.3s ease;
}

.step.completed + .step-divider {
  background: #10b981;
}

/* Step Content */
.step-content {
  padding: 32px;
  min-height: 500px;
}

.step-header {
  text-align: center;
  margin-bottom: 32px;
}

.step-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-description {
  font-size: 16px;
  color: #64748b;
  margin: 0;
}

/* Agent Selection Step */
.agents-section {
  max-width: 1000px;
  margin: 0 auto;
}

.agents-toolbar {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.search-container {
  flex: 1;
}

.search-input {
  margin: 0;
}

.selection-info {
  flex-shrink: 0;
}

.selection-chip {
  font-weight: 600;
}

.agents-container {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
}

.agent-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 160px;
  display: flex;
  flex-direction: column;
}

.agent-card:hover {
  border-color: #667eea;
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.15);
}

.agent-card.selected {
  border-color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%);
  box-shadow: 0 8px 30px rgba(16, 185, 129, 0.15);
}

.agent-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.agent-checkbox {
  margin: 0;
}

.selected-badge {
  position: absolute;
  top: 12px;
  right: 12px;
}

.agent-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.agent-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.agent-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1.3;
}

.agent-industry {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
}

.agent-pricing {
  margin-top: auto;
}

.agent-pricing .price {
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
}

.agent-pricing .period {
  font-size: 12px;
  color: #64748b;
  margin-left: 2px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.loading-state h3,
.empty-state h3 {
  margin: 16px 0 8px 0;
  color: #2c3e50;
}

/* Configuration Step */
.config-section {
  max-width: 800px;
  margin: 0 auto;
}

.config-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  height: 100%;
}

.config-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.config-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: #667eea;
  border-radius: 2px;
  margin-right: 12px;
}

.config-input {
  margin-bottom: 16px;
}

/* Review Step */
.review-section {
  max-width: 1000px;
  margin: 0 auto;
}

.review-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.review-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.selected-agents-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.selected-agent-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.selected-agent-item .agent-details {
  flex: 1;
  margin-left: 0;
}

.selected-agent-item .agent-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 4px 0;
}

.selected-agent-item .agent-industry {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.selected-agent-item .agent-price {
  text-align: right;
}

.selected-agent-item .agent-price .price {
  font-size: 16px;
  font-weight: 600;
  color: #10b981;
}

.selected-agent-item .agent-price .period {
  font-size: 12px;
  color: #64748b;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.detail-label {
  font-weight: 600;
  color: #64748b;
}

.detail-value {
  font-weight: 500;
  color: #2c3e50;
}

.pricing-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.3);
  position: sticky;
  top: 20px;
}

.pricing-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  color: white;
}

.pricing-breakdown {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
}

.pricing-row:not(.total) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.pricing-row.total {
  font-size: 18px;
  font-weight: 700;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 2px solid rgba(255, 255, 255, 0.3);
}

.pricing-row .amount {
  font-weight: 600;
}

.pricing-row.discount .amount {
  color: #fef3c7;
}

.pricing-row.total .amount {
  font-size: 20px;
  color: #dcfce7;
}

.pricing-period {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
}

/* Navigation */
.dialog-navigation {
  background: white;
  border-top: 1px solid #e2e8f0;
  padding: 20px 32px;
}

.nav-buttons {
  display: flex;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto;
}

.nav-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 44px !important;
  padding: 0 24px !important;
}

.nav-btn.v-btn--outlined {
  border-width: 2px !important;
}

.nav-btn.v-btn--contained {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Minimal Input for Custom Dialog */
.custom-dialog .minimal-input >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
  padding: 0 12px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.custom-dialog .minimal-input >>> .v-input__control .v-input__slot:before,
.custom-dialog .minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.custom-dialog .minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
}

.custom-dialog .minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1) !important;
}

/* Remove fieldset borders for custom dialog */
.custom-dialog .minimal-input >>> fieldset {
  display: none !important;
}

/* Responsive for Custom Dialog */
@media (max-width: 1024px) {
  .custom-dialog {
    max-width: 95vw !important;
    margin: 20px;
  }
  
  .agents-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
  
  .step-content {
    padding: 24px 20px;
  }
  
  .agents-toolbar {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .search-container {
    width: 100%;
  }
  
  .pricing-card {
    position: static;
    margin-top: 20px;
  }
}

@media (max-width: 768px) {
  .stepper-header {
    flex-direction: column;
    gap: 20px;
  }
  
  .step {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
  
  .step-number {
    margin-bottom: 0;
  }
  
  .step-divider {
    display: none;
  }
  
  .step-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }
  
  .agents-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
  
  .agent-card {
    min-height: 140px;
    padding: 16px;
  }
  
  .config-section .v-col {
    margin-bottom: 20px;
  }
  
  .nav-buttons {
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
  }
  
  .nav-btn {
    min-width: 120px;
  }
}

@media (max-width: 600px) {
  .progress-stepper {
    padding: 16px;
  }
  
  .step-content {
    padding: 20px 16px;
    min-height: 400px;
  }
  
  .agents-container {
    padding: 16px;
  }
  
  .agents-grid {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  
  .agent-card {
    min-height: 120px;
    padding: 12px;
  }
  
  .agent-name {
    font-size: 14px;
  }
  
  .agent-industry {
    font-size: 11px;
  }
  
  .agent-pricing .price {
    font-size: 16px;
  }
  
  .config-card {
    padding: 20px;
  }
  
  .review-card {
    padding: 20px;
  }
  
  .selected-agent-item {
    padding: 12px;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
  
  .selected-agent-item .agent-details {
    margin-left: 0;
  }
  
  .pricing-card {
    padding: 20px;
  }
  
  .dialog-navigation {
    padding: 16px 20px;
  }
  
  .nav-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .nav-btn {
    width: 100%;
    order: 1;
  }
  
  .nav-btn:first-child {
    order: 2;
  }
}

/* Notification Snackbar */
.notification-snackbar {
  z-index: 9999 !important;
}

.notification-snackbar .v-snack__wrapper {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  backdrop-filter: blur(10px);
}

.notification-snackbar .v-snack__content {
  font-weight: 500;
  font-size: 14px;
  padding: 16px 20px;
}
</style>